import { createFileRoute } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { getTemplates, createTemplate, updateTemplate, deleteTemplate } from '../../lib/api'
import { keys } from '../../lib/queryKeys'
import { PageHeader } from '../../components/PageHeader'
import { Entry, EntryList } from '../../components/Entry'
import { m } from '../../paraglide/messages'
import type { NotificationTemplate } from '../../types'

export const Route = createFileRoute('/templates/')({
  component: TemplatesPage,
})

type FormValues = {
  name:            string
  trigger:         'lesson_time' | 'lesson_created'
  offsetMin:       string
  offsetFromEnd:   boolean
  recipient:       'tutor' | 'student' | 'both'
  method:          'whatsapp' | 'browser' | 'both'
  messageTemplate: string
  active:          boolean
}

const emptyForm: FormValues = {
  name:            '',
  trigger:         'lesson_time',
  offsetMin:       '-60',
  offsetFromEnd:   false,
  recipient:       'student',
  method:          'whatsapp',
  messageTemplate: '',
  active:          true,
}

function toForm(t: NotificationTemplate): FormValues {
  return {
    name:            t.name,
    trigger:         t.trigger ?? 'lesson_time',
    offsetMin:       String(t.offsetMin),
    offsetFromEnd:   t.offsetFromEnd,
    recipient:       t.recipient,
    method:          t.method,
    messageTemplate: t.messageTemplate,
    active:          t.active,
  }
}

function formatOffset(t: NotificationTemplate) {
  if (t.trigger === 'lesson_created') return m.templates_trigger_lesson_created()
  const sign  = t.offsetMin >= 0 ? '+' : ''
  const label = m.duration_min({ count: `${sign}${t.offsetMin}` })
  return t.offsetFromEnd ? `${label} ${m.templates_offset_from_end()}` : label
}

const recipientLabel: Record<string, () => string> = {
  tutor:   () => m.recipient_tutor(),
  student: () => m.recipient_student(),
  both:    () => m.recipient_both(),
}

const methodLabel: Record<string, () => string> = {
  whatsapp: () => m.method_whatsapp(),
  browser:  () => m.method_browser(),
  both:     () => m.method_both(),
}

function TemplatesPage() {
  const queryClient = useQueryClient()
  const [showNewForm, setShowNewForm] = useState(false)
  const [newForm, setNewForm]         = useState(emptyForm)
  const [newError, setNewError]       = useState('')
  const [editingId, setEditingId]     = useState<number | null>(null)
  const [editForm, setEditForm]       = useState<FormValues>(emptyForm)
  const [editError, setEditError]     = useState('')

  const { data, isLoading, error } = useQuery<NotificationTemplate[]>({
    queryKey: keys.templates,
    queryFn:  getTemplates,
  })

  const createMutation = useMutation({
    mutationFn: createTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.templates })
      setShowNewForm(false)
      setNewForm(emptyForm)
      setNewError('')
    },
    onError: (err) => setNewError((err as Error).message),
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, body }: { id: number; body: Record<string, unknown> }) =>
      updateTemplate(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.templates })
      setEditingId(null)
      setEditError('')
    },
    onError: (err) => setEditError((err as Error).message),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.templates })
      setEditingId(null)
    },
  })

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault()
    setNewError('')
    createMutation.mutate({ ...newForm, offsetMin: Number(newForm.offsetMin) })
  }

  const handleUpdate = (e: React.FormEvent) => {
    e.preventDefault()
    if (editingId == null) return
    setEditError('')
    updateMutation.mutate({ id: editingId, body: { ...editForm, offsetMin: Number(editForm.offsetMin) } })
  }

  const startEdit = (t: NotificationTemplate) => {
    setShowNewForm(false)
    setEditingId(t.id)
    setEditForm(toForm(t))
    setEditError('')
  }

  if (isLoading) return <p className="text-zinc-400">{m.loading()}</p>
  if (error) return <p className="text-red-600 text-sm">{(error as Error).message}</p>

  const templates = data ?? []

  return (
    <div>
      <PageHeader title={m.templates_title()}>
        {!showNewForm && editingId == null && (
          <button
            onClick={() => setShowNewForm(true)}
            className="bg-zinc-900 text-white px-3 py-1.5 text-sm hover:bg-zinc-700"
          >
            {m.templates_new()}
          </button>
        )}
      </PageHeader>

      {/* New template form */}
      {showNewForm && (
        <form onSubmit={handleCreate} className="mb-6 border border-zinc-200 p-4">
          <p className="text-xs text-zinc-400 uppercase tracking-wide mb-3">{m.templates_new_heading()}</p>
          <TemplateFormFields
            values={newForm}
            onChange={(f) => setNewForm((prev) => ({ ...prev, ...f }))}
          />
          {newError && <p className="text-red-600 text-sm mt-2">{newError}</p>}
          <div className="flex gap-2 mt-3">
            <button
              type="submit"
              disabled={createMutation.isPending}
              className="bg-zinc-900 text-white px-3 py-1.5 text-sm hover:bg-zinc-700 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {createMutation.isPending ? m.saving() : m.action_save()}
            </button>
            <button
              type="button"
              onClick={() => { setShowNewForm(false); setNewForm(emptyForm); setNewError('') }}
              className="border border-zinc-900 px-3 py-1.5 text-sm hover:bg-zinc-100"
            >
              {m.action_cancel()}
            </button>
          </div>
        </form>
      )}

      {/* Edit template form (stacked, mobile-friendly) */}
      {editingId != null && (
        <form onSubmit={handleUpdate} className="mb-6 border border-zinc-200 p-4">
          <p className="text-xs text-zinc-400 uppercase tracking-wide mb-3">{m.templates_edit_heading()}</p>
          <TemplateFormFields
            values={editForm}
            onChange={(f) => setEditForm((prev) => ({ ...prev, ...f }))}
          />
          <label className="flex items-center gap-2 text-sm text-zinc-700 mt-3">
            <input
              type="checkbox"
              checked={editForm.active}
              onChange={(e) => setEditForm((v) => ({ ...v, active: e.target.checked }))}
            />
            {m.templates_active()}
          </label>
          {editError && <p className="text-red-600 text-sm mt-2">{editError}</p>}
          <div className="flex flex-wrap gap-2 mt-3">
            <button
              type="submit"
              disabled={updateMutation.isPending}
              className="bg-zinc-900 text-white px-3 py-1.5 text-sm hover:bg-zinc-700 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {updateMutation.isPending ? m.saving() : m.action_save()}
            </button>
            <button
              type="button"
              onClick={() => { setEditingId(null); setEditError('') }}
              className="border border-zinc-900 px-3 py-1.5 text-sm hover:bg-zinc-100"
            >
              {m.action_cancel()}
            </button>
            <button
              type="button"
              onClick={() => {
                if (confirm(m.templates_delete_confirm())) {
                  deleteMutation.mutate(editingId)
                }
              }}
              disabled={deleteMutation.isPending}
              className="ms-auto border border-zinc-300 text-zinc-500 px-3 py-1.5 text-sm hover:border-red-600 hover:text-red-600 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {deleteMutation.isPending ? m.deleting() : m.action_delete()}
            </button>
          </div>
        </form>
      )}

      {/* Templates list */}
      <EntryList isEmpty={templates.length === 0} emptyMessage={m.templates_empty()}>
        {templates.map((t) => (
          <Entry
            key={t.id}
            title={t.name}
            meta={`${formatOffset(t)} · ${recipientLabel[t.recipient]?.() ?? t.recipient} · ${methodLabel[t.method]?.() ?? t.method}`}
            submeta={t.messageTemplate}
            status={t.active ? undefined : <span className="text-zinc-400">{m.job_inactive()}</span>}
            actions={
              <>
                <input
                  type="checkbox"
                  aria-label={m.toggle_active({ name: t.name })}
                  checked={t.active}
                  onChange={(e) =>
                    updateMutation.mutate({ id: t.id, body: { active: e.target.checked } })
                  }
                  className="cursor-pointer"
                />
                <button
                  onClick={() => startEdit(t)}
                  className="border border-zinc-900 px-3 py-1.5 text-sm hover:bg-zinc-100"
                >
                  {m.action_edit()}
                </button>
              </>
            }
          />
        ))}
      </EntryList>
    </div>
  )
}

// ─── Template Form Fields (shared between new & edit) ────────────────────────

function TemplateFormFields({
  values,
  onChange,
}: {
  values:   FormValues
  onChange: (patch: Partial<FormValues>) => void
}) {
  const fieldCls = 'border border-zinc-300 px-2 py-1.5 text-sm w-full focus:outline-none focus:border-zinc-900'

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-sm text-zinc-700">{m.jobs_name()}</span>
          <input
            type="text"
            required
            value={values.name}
            onChange={(e) => onChange({ name: e.target.value })}
            className={fieldCls}
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-sm text-zinc-700">{m.templates_trigger()}</span>
          <select
            value={values.trigger}
            onChange={(e) => onChange({ trigger: e.target.value as FormValues['trigger'] })}
            className={fieldCls}
          >
            <option value="lesson_time">{m.templates_trigger_lesson_time()}</option>
            <option value="lesson_created">{m.templates_trigger_lesson_created()}</option>
          </select>
        </label>
        {values.trigger === 'lesson_time' && (
          <label className="flex flex-col gap-1">
            <span className="text-sm text-zinc-700">{m.templates_offset()}</span>
            <input
              type="number"
              required
              value={values.offsetMin}
              onChange={(e) => onChange({ offsetMin: e.target.value })}
              className={fieldCls}
            />
          </label>
        )}
        <label className="flex flex-col gap-1">
          <span className="text-sm text-zinc-700">{m.templates_recipient()}</span>
          <select
            value={values.recipient}
            onChange={(e) => onChange({ recipient: e.target.value as FormValues['recipient'] })}
            className={fieldCls}
          >
            <option value="tutor">{m.recipient_tutor()}</option>
            <option value="student">{m.recipient_student()}</option>
            <option value="both">{m.recipient_both()}</option>
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-sm text-zinc-700">{m.templates_method()}</span>
          <select
            value={values.method}
            onChange={(e) => onChange({ method: e.target.value as FormValues['method'] })}
            className={fieldCls}
          >
            <option value="whatsapp">{m.method_whatsapp()}</option>
            <option value="browser">{m.method_browser()}</option>
            <option value="both">{m.method_both()}</option>
          </select>
        </label>
      </div>

      {values.trigger === 'lesson_time' && (
        <label className="flex items-center gap-2 text-sm text-zinc-700">
          <input
            type="checkbox"
            checked={values.offsetFromEnd}
            onChange={(e) => onChange({ offsetFromEnd: e.target.checked })}
          />
          {m.templates_from_end()}
        </label>
      )}

      <label className="flex flex-col gap-1">
        <span className="text-sm text-zinc-700">
          {m.templates_message_label()}{' '}
          <span className="text-zinc-400">{m.templates_vars_hint()}</span>
        </span>
        <textarea
          rows={3}
          required
          value={values.messageTemplate}
          onChange={(e) => onChange({ messageTemplate: e.target.value })}
          className={fieldCls}
        />
      </label>
    </div>
  )
}
